import angular from 'angular'
import { FeaturesResponse } from '../../react/domain/features/featuresApi'
import {
  evaluateTreatment,
  FeatureFlag,
  isTreatmentOn,
} from '../../react/domain/features/featureFlags'

type CurrentUser = {
  tenant?: { id?: number }
}

type UserApi = {
  getCurrentUser(): angular.IPromise<CurrentUser | undefined>
}

type Env = {
  apiEndpoint: string
}

type RequestService = {
  get<T>(url: string): angular.IPromise<{ data: T }>
  post(url: string, body: unknown): angular.IPromise<unknown>
}

type FeatureContext = {
  tenantId: number
  features: FeatureFlag[]
  mySegments: string[]
}

export type FeatureFlagService = {
  exampleFeature(): angular.IPromise<boolean>
  jobDetails(): angular.IPromise<boolean>
  jobSchedule(): angular.IPromise<boolean>
  reactJobDetail(): angular.IPromise<boolean>
  reactCustomers(): angular.IPromise<boolean>
}

export function FeatureFlagServiceFactory(
  $q: angular.IQService,
  userApi: UserApi,
  ENV: Env,
  request: RequestService
): FeatureFlagService {

  const reportedEvaluations = new Set<string>()

  const context = $q<FeatureContext>((resolve) => {
    userApi.getCurrentUser()
      .then((user) => {
        const tenantId = user?.tenant?.id ?? 0
        return request.get<FeaturesResponse>(`${ENV.apiEndpoint}/features`).then((response) => {
          resolve({ tenantId, features: response.data.features, mySegments: response.data.mySegments })
        })
      })
      .catch(() => {
        resolve({ tenantId: 0, features: [], mySegments: [] })
      })
  })

  function reportUsage(featureId: string, treatment: string) {
    const evaluationKey = `${featureId}:${treatment}`
    if (reportedEvaluations.has(evaluationKey)) {
      return
    }
    reportedEvaluations.add(evaluationKey)
    request.post(`${ENV.apiEndpoint}/features/traffic`, { featureId, treatment }).catch(() => {})
  }

  function isFeatureEnabledAsync(featureName: string): angular.IPromise<boolean> {
    return context.then(({ tenantId, features, mySegments }) => {
      const treatment = evaluateTreatment(features, featureName, tenantId, mySegments, 'control')
      reportUsage(featureName, treatment)
      return isTreatmentOn(treatment)
    })
  }

  return {
    exampleFeature: () => isFeatureEnabledAsync('example-feature'),
    jobDetails: () => isFeatureEnabledAsync('job-details'),
    jobSchedule: () => isFeatureEnabledAsync('job-schedule'),
    reactJobDetail: () => isFeatureEnabledAsync('react-job-detail'),
    reactCustomers: () => isFeatureEnabledAsync('react-customers'),
  }
}

FeatureFlagServiceFactory.$inject = ['$q', 'userApi', 'ENV', 'request']

angular.module('app').service('featureFlagService', FeatureFlagServiceFactory)
